import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	Dispatch,
	ReactElement,
	ReadonlyRefObject,
	SetStateAction,
	useCallback,
	useEffect
} from "react";
import { useUploadNameConflictResolutionDialog } from "../Dialogs/UploadNameConflictResolutionDialog/UploadNameConflictResolutionDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useSynchronizedRef } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { NameConflictResolutionKind } from "client/Api/Documents/common";
import { FontIconName, SvgIconValue } from "client/Components";

export type UseDocumentUploaderResult = {
	deleteHandler: (id: Guid) => void;
	retryHandler: (id: Guid) => void;
	addHandler: (
		files: readonly FileSelectionEntry[],
		handlers: UploadBatchHandlers
	) => void;
	nameConflictDialog: ReactElement | null;
};

export type UploadChunkInfo = {
	fileGuid: Guid;
	fileName: string;
	path: string;
	totalParts: number;
	partIndex: number;
	partByteOffset: number;
	chunk: Blob;
	nameConflictResolution: NameConflictResolutionKind | undefined;
};

export type UploadFileInfo = {
	fileGuid: Guid;
	fileName: string;
	path: string;
	file: File;
	nameConflictResolution: NameConflictResolutionKind | undefined;
};

export type UploadBatchFileHandlers = {
	uploadFile: (data: UploadFileInfo, signal: AbortSignal) => Promise<UploaderItemResult>;
};

export type UploadBatchChunkHandlers = UploadBatchFileHandlers & {
	uploadChunk: (
		chunkInfo: UploadChunkInfo,
		signal: AbortSignal
	) => Promise<UploaderItemResult>;
	cancelChunkedUpload: (
		fileUploadGuid: Guid,
		fileName: string,
		path: string
	) => Promise<void>;
};

export type UploadBatchHandlers = UploadBatchFileHandlers | UploadBatchChunkHandlers;

export function useDocumentUploader(
	entries: readonly UploadCacheItem[],
	setEntries: Dispatch<SetStateAction<readonly UploadCacheItem[]>>
): UseDocumentUploaderResult {
	const [nameConflictDialog, openNameConflictDialog] = useUploadNameConflictResolutionDialog();

	const entriesRef: ReadonlyRefObject<readonly UploadCacheItem[]> = useSynchronizedRef(entries);

	const changeState = useCallback(
		(id: Guid, state: UploadCacheItemState): void => {
			setEntries(
				(x) => {
					const copy: UploadCacheItem[] = [...x];

					const index: number = copy.findIndex(x => x.id === id);

					if (index === -1) {
						throw new Error(`Cannot find cache item for '${id}'`);
					}

					const cache: UploadCacheItem = copy[index];

					copy.splice(
						index,
						1,
						{
							...cache,
							state,
							entry: {
								...cache.entry,
								file: state.kind === "success"
									// Clear the reference to the file so the GC can collect it
									? null
									: cache.entry
										.file
							}
						}
					);

					return copy;
				}
			);
		},
		[setEntries]
	);

	const setNameConflictResolutionForItem = useCallback(
		(id: Guid, resolution: NameConflictResolutionKind): void => {
			setEntries(
				(x) => {
					const copy: UploadCacheItem[] = [...x];

					const index: number = copy.findIndex(x => x.id === id);

					if (index === -1) {
						throw new Error(`Cannot find cache item for '${id}'`);
					}

					const cache: UploadCacheItem = copy[index];

					copy.splice(
						index,
						1,
						{
							...cache,
							nameConflictResolution: resolution
						}
					);

					return copy;
				}
			);
		},
		[setEntries]
	);

	const setNameConflictResolutionForPendingItemsInBatch = useCallback(
		(batchId: Guid, resolution: NameConflictResolutionKind): void => {
			setEntries(
				(x) => {
					return x.map((x) => {
						if (x.entry.batchId === batchId
							&& x.state.kind === "pending"
						) {
							return {
								...x,
								nameConflictResolution: resolution
							};
						} else {
							return x;
						}
					});
				}
			);
		},
		[setEntries]
	);

	const uploadInChunks = useCallback(
		async (
			id: Guid,
			data: UploadFileInfo,
			state: UploadingState,
			handlers: UploadBatchChunkHandlers,
			signal: AbortSignal
		): Promise<UploaderItemResult> => {
			const {
				file,
				fileGuid,
				path,
				nameConflictResolution
			} = data;

			const totalChunks: number = Math.ceil(file.size / CHUNK_SIZE_IN_BYTES);

			let fileName: string = data.fileName;
			let result: UploaderItemResult = { kind: "failed" };

			for (let i = 0; i < totalChunks; i++) {
				const chunkStart: number = i * CHUNK_SIZE_IN_BYTES;
				let chunkEnd: number = chunkStart + CHUNK_SIZE_IN_BYTES;
				if (chunkEnd > file.size) {
					chunkEnd = file.size;
				}
				const chunk: Blob = file.slice(chunkStart, chunkEnd);

				const progress: number = ((i + 1) / totalChunks) * 100;

				changeState(
					id,
					{
						...state,
						progress
					}
				);

				try {
					result = await handlers.uploadChunk(
						{
							fileName,
							path,
							chunk,
							fileGuid,
							totalParts: totalChunks,
							partByteOffset: chunkStart,
							partIndex: i,
							nameConflictResolution
						},
						signal
					);

					switch (result.kind) {
						case "success":
							if (isNotNil(result.uploadedFileName)) {
								fileName = result.uploadedFileName;
							}
							break;
						case "name-conflict":
						case "failed":
							handlers.cancelChunkedUpload(
								state.sessionId,
								data.fileName,
								data.path
							);
							return result;
						default:
							never(result);
					}
				} catch (e) {
					handlers.cancelChunkedUpload(
						state.sessionId,
						data.fileName,
						data.path
					);

					throw e;
				}
			}

			return result;
		},
		[changeState]
	);

	const uploadAsSingleRequest = useCallback(
		(
			data: UploadFileInfo,
			handlers: UploadBatchHandlers,
			signal: AbortSignal,
		): Promise<UploaderItemResult> => {
			return handlers.uploadFile(
				data,
				signal
			);
		},
		[]
	);

	const deleteHandler = useCallback(
		(id: Guid): void => {
			setEntries(
				(x) => {
					const copy: UploadCacheItem[] = [...x];

					const index: number = copy.findIndex(x => x.id === id);

					if (index === -1) {
						throw new Error(`Cannot find cache item for '${id}'`);
					}

					const cache: UploadCacheItem = copy[index];

					const { state } = cache;
					if (state.kind === "uploading") {
						state.cancellation
							.abort();
					}

					copy.splice(index, 1);

					return copy;
				}
			);
		},
		[setEntries]
	);

	const tryHandleNameConflict = useCallback(
		async (
			id: Guid,
			cache: UploadCacheItem
		): Promise<boolean> => {
			switch (cache.nameConflictResolution) {
				case undefined:
					try {
						const { resolution, applyToRemaining } = await openNameConflictDialog({
							itemName: cache.entry.name,
							hasMoreItemsInBatch: entriesRef.current
								.some(x => x.state.kind === "pending")
						});

						setNameConflictResolutionForItem(id, resolution);

						if (applyToRemaining) {
							setNameConflictResolutionForPendingItemsInBatch(
								cache.entry
									.batchId,
								resolution
							);
						}

						switch (resolution) {
							case NameConflictResolutionKind.KeepBoth:
							case NameConflictResolutionKind.Replace:
								changeState(id, createPendingState());
								break;
							case NameConflictResolutionKind.Skip:
								deleteHandler(id);
								break;
							default:
								never(resolution);
						}

						return true;
					} catch (e) {
						console.error(e);
						return false;
					}
				case NameConflictResolutionKind.Skip:
					deleteHandler(id);
					return true;
				case NameConflictResolutionKind.KeepBoth:
				case NameConflictResolutionKind.Replace:
					return false;
				default:
					never(cache.nameConflictResolution);
			}
		},
		[
			changeState,
			entriesRef,
			openNameConflictDialog,
			setNameConflictResolutionForItem,
			setNameConflictResolutionForPendingItemsInBatch,
			deleteHandler
		]
	);

	const doUpload = useCallback(
		async (cache: UploadCacheItem): Promise<void> => {
			if (!cache || cache.state.kind !== "pending") {
				console.warn(`Upload '${cache.id}' is not in the pending state to start the upload`);
				return;
			}

			const sessionId: Guid = newGuid();
			const {
				entry: {
					file,
					path
				},
				handlers,
				nameConflictResolution
			} = cache;

			if (!file) {
				throw new Error(`Upload '${cache.id}' File was already disposed for GC`);
			}

			const cancellation = new AbortController();

			const uploadingState: UploadingState = createUploadingState(sessionId, cancellation);

			const id: Guid = cache.id;

			changeState(cache.id, uploadingState);

			const data: UploadFileInfo = {
				fileGuid: sessionId,
				fileName: file.name,
				path: extractDirectoryPath(path, file.name),
				file,
				nameConflictResolution
			};

			let result: UploaderItemResult;
			try {
				if (hasChunkHandler(handlers) && file.size > CHUNK_SIZE_IN_BYTES) {
					result = await uploadInChunks(
						id,
						data,
						uploadingState,
						handlers,
						cancellation.signal
					);
				} else {
					result = await uploadAsSingleRequest(
						data,
						handlers,
						cancellation.signal
					);
				}

				if (cancellation.signal
					.aborted
				) {
					return;
				}

				switch (result.kind) {
					case "name-conflict":
						const wasHandled: boolean = await tryHandleNameConflict(id, cache);

						if (!wasHandled) {
							changeState(id, createFailedState(result.message));
						}

						return;
					case "failed":
						changeState(
							id,
							createFailedState(result.message)
						);
						return;
					case "success":
						const newPath: string | null = isNotNilOrEmpty(result.uploadedFileName)
							? data.path + result.uploadedFileName
							: null;

						changeState(
							id,
							createSuccessState(newPath, result.uploadedFileActions)
						);
						return;
					default:
						never(result);
				}

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				changeState(
					id,
					createFailedState()
				);
			}
		},
		[
			changeState,
			tryHandleNameConflict,
			uploadAsSingleRequest,
			uploadInChunks
		]
	);

	const addHandler = useCallback(
		(
			files: readonly FileSelectionEntry[],
			handlers: UploadBatchHandlers
		): void => {
			const batchId: Guid = newGuid();

			const uploadEntries: UploadEntry[] = files
				.map((x) => ({
					...x,
					name: x.file
						.name,
					size: x.file
						.size,
					batchId
				}));

			setEntries(
				(x) => {
					const copy: UploadCacheItem[] = [...x];

					for (const entry of uploadEntries) {
						copy.push({
							id: newGuid(),
							entry,
							state: createPendingState(),
							handlers,
							nameConflictResolution: undefined
						});
					}

					return copy;
				}
			);
		},
		[setEntries]
	);

	const retryHandler = useCallback(
		(id: Guid): void => {
			setEntries(
				(x) => {
					const copy: UploadCacheItem[] = [...x];

					const index: number = copy.findIndex(x => x.id === id);

					if (index === -1) {
						throw new Error(`Cannot find cache item for '${id}'`);
					}

					const cache: UploadCacheItem = copy[index];

					if (cache.state
						.kind === "failed"
					) {
						copy.splice(
							index,
							1,
							{
								...cache,
								entry: {
									...cache.entry,
									batchId: newGuid()
								},
								state: createPendingState(),
								nameConflictResolution: undefined
							}
						);
					}

					return copy;
				}
			);
		},
		[setEntries]
	);

	useEffect(
		(): void => {
			const isAnyUploading: boolean = entries.some(
				x => x.state
					.kind === "uploading"
			);

			if (isAnyUploading) {
				return;
			}

			const entryToStartUploading: UploadCacheItem | undefined = entries.find(
				x => x.state
					.kind === "pending"
			);

			if (entryToStartUploading) {
				doUpload(entryToStartUploading);
			}
		},
		[doUpload, entries]
	);

	return {
		addHandler,
		deleteHandler,
		retryHandler,
		nameConflictDialog
	};
}

export type UploadEntry = {
	batchId: Guid;
	size: number;
	name: string;
	file: File | null;
	path: string;
};

export type PendingState = { kind: "pending"; };

export function createPendingState(): PendingState {
	return { kind: "pending" };
}

export type UploadingState = {
	kind: "uploading";
	sessionId: Guid;
	cancellation: AbortController;
	progress: number | null;
};

function createUploadingState(
	sessionId: Guid,
	cancellation: AbortController,
	progress: number | null = null
): UploadingState {
	return {
		kind: "uploading",
		sessionId,
		cancellation,
		progress
	};
}

export type FailedState = {
	kind: "failed";
	message: string | undefined;
};

function createFailedState(message?: string): FailedState {
	return {
		kind: "failed",
		message
	};
}

export type SuccessState = {
	kind: "success";
	path: string | null;
	uploadedFileActions?: UploaderUploadedFileActions;
};

function createSuccessState(
	path: string | null,
	uploadedFileActions?: UploaderUploadedFileActions
): SuccessState {
	return {
		kind: "success",
		path,
		uploadedFileActions
	};
}

export type UploadCacheItemState =
	| PendingState
	| UploadingState
	| FailedState
	| SuccessState;

export type UploadCacheItem = {
	id: Guid;
	entry: UploadEntry;
	state: UploadCacheItemState;
	handlers: UploadBatchHandlers;
	nameConflictResolution: NameConflictResolutionKind | undefined;
};

type UploaderUploadedFileCustomAction = {
	name: string;
	title: string;
	icon: FontIconName | SvgIconValue;
	execute: () => void;
};

export type UploaderUploadedFileActions = {
	openFile?: () => void;
	otherActions?: readonly UploaderUploadedFileCustomAction[];
};

export type UploaderItemResult = {
	kind: "failed";
	message?: string;
} | {
	kind: "success";
	uploadedFileName: string | null;
	uploadedFileActions?: UploaderUploadedFileActions;
} | {
	kind: "name-conflict";
	message?: string;
};

const CHUNK_SIZE_IN_BYTES = 10485760;

function extractDirectoryPath(path: string, filename: string): string {
	const indexOfFilename: number = path.indexOf(filename);
	if (indexOfFilename === -1) {
		console.warn(`Path '${path}' does not contain filename '${filename}'`);
		return "";
	}

	let directoryPath: string = path.substring(0, indexOfFilename);
	if (directoryPath[0] === "/") {
		directoryPath = directoryPath.substring(1);
	}
	return directoryPath;
}

export function hasChunkHandler(
	handlers: UploadBatchHandlers
): handlers is UploadBatchChunkHandlers {
	return isNotNil(
		(handlers as UploadBatchChunkHandlers).uploadChunk
	);
}